/** Procurement Readiness (Demo Mode only) - the guided Stage 5 workspace for preparing a
 * customer site's procurement so it can order, receive and use the right ingredients, products
 * and consumables. Landing page lists sites with their readiness status/blockers; opening a
 * site goes to the guided site workspace (see ./SiteWorkspace.tsx). All figures are static mock
 * data - see src/mocks/data/procurementReadinessData.ts. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Grid, LinearProgress, MenuItem, Stack, SvgIcon, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, useTheme,
} from '@mui/material';
import {
  Block, CheckCircleOutline, HourglassTop, PlaylistAddCheck, Storefront,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import {
  READINESS_STATUS_COLOR, ReadinessStatus, siteProcurementReadiness,
} from '../../../mocks/data/procurementReadinessData';

const ALL = 'All';
const TILE_COLORS = ['#2e7d32', '#ed6c02', '#d32f2f', '#1976d2'];

const ProcurementReadinessPage = () => {
  const { t } = useTranslation('procurementReadiness');
  const navigate = useNavigate();
  const theme = useTheme();
  const [clientAccount, setClientAccount] = useState<string>(ALL);
  const [status, setStatus] = useState<ReadinessStatus | typeof ALL>(ALL);

  const clientAccounts = useMemo(
    () => Array.from(new Set(siteProcurementReadiness.map((s) => s.clientAccount))),
    [],
  );

  const rows = useMemo(
    () => siteProcurementReadiness.filter((site) => (clientAccount === ALL || site.clientAccount === clientAccount)
      && (status === ALL || site.status === status)),
    [clientAccount, status],
  );

  const readyForGoLive = siteProcurementReadiness.filter((s) => s.status === 'Ready for go-live' || s.status === 'Approved').length;
  const inProgress = siteProcurementReadiness.filter((s) => !['Ready for go-live', 'Approved', 'Live', 'Not started'].includes(s.status)).length;
  const blocked = siteProcurementReadiness.filter((s) => s.blockers.length > 0).length;
  const notStarted = siteProcurementReadiness.filter((s) => s.status === 'Not started').length;

  const TILES = [
    { key: 'ready', label: t('tileReadyForGoLive', 'Ready for go-live'), value: readyForGoLive, subtitle: t('tileReadyForGoLiveSubtitle', 'Approved / ready to launch'), icon: <CheckCircleOutline /> },
    { key: 'inProgress', label: t('tileInProgress', 'In progress'), value: inProgress, subtitle: t('tileInProgressSubtitle', 'Being prepared'), icon: <HourglassTop /> },
    { key: 'blocked', label: t('tileBlockedSites', 'Blocked sites'), value: blocked, subtitle: t('tileBlockedSitesSubtitle', 'Have open blockers'), icon: <Block /> },
    { key: 'notStarted', label: t('tileNotStarted', 'Not started'), value: notStarted, subtitle: t('tileNotStartedSubtitle', 'Awaiting approved range'), icon: <Storefront /> },
  ];

  const overallReadiness = (site: typeof siteProcurementReadiness[number]) => Math.round(
    (site.supplierReadinessPct + site.catalogueReadinessPct + site.productReadinessPct
      + site.deliveryReadinessPct + site.openingStockReadinessPct) / 5,
  );

  return (
    <PageArea>
      <PageTitle subTitle={t('pageSubtitle', 'Guided Stage 5 workspace - move each site from approved range and site requirements to a fully approved procurement setup')}>
        {t('pageTitle', 'Procurement Readiness')}
      </PageTitle>

      <Grid container
        spacing={2}
        sx={{ mt: 2, mb: 4 }}>
        {TILES.map((tile, index) => (
          <Grid item
            key={tile.key}
            xs={6}
            md={3}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{
                  backgroundColor: `${TILE_COLORS[index % TILE_COLORS.length]}20`,
                  borderRadius: '50%',
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: TILE_COLORS[index % TILE_COLORS.length],
                }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Grid container
        spacing={2}
        sx={{ mb: 3 }}>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <TextField select
            label={t('filterClientAccount', 'Client account')}
            value={clientAccount}
            onChange={(e) => setClientAccount(e.target.value)}
            fullWidth
            size="small"
          >
            <MenuItem value={ALL}>{t('filterAllClientAccounts', 'All client accounts')}</MenuItem>
            {clientAccounts.map((account) => (
              <MenuItem key={account}
                value={account}>{account}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item
          xs={12}
          sm={6}
          md={3}>
          <TextField select
            label={t('filterReadinessStatus', 'Readiness status')}
            value={status}
            onChange={(e) => setStatus(e.target.value as ReadinessStatus | typeof ALL)}
            fullWidth
            size="small"
          >
            <MenuItem value={ALL}>{t('filterAllStatuses', 'All statuses')}</MenuItem>
            {Object.keys(READINESS_STATUS_COLOR).map((s) => (
              <MenuItem key={s}
                value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Section>
        <TableHeader>
          {t('sitesTableTitle', 'Sites')}
          <Pill text={t('siteCount', '{{count}} sites', { count: rows.length })} />
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('columnSite', 'Site')}</TableCell>
                <TableCell>{t('columnClientAccount', 'Client account')}</TableCell>
                <TableCell>{t('columnStatus', 'Status')}</TableCell>
                <TableCell>{t('columnOverallReadiness', 'Overall readiness')}</TableCell>
                <TableCell>{t('columnOpenExceptions', 'Open exceptions')}</TableCell>
                <TableCell>{t('columnNextBestAction', 'Next best action')}</TableCell>
                <TableCell>{t('columnOwner', 'Owner')}</TableCell>
                <TableCell>{t('columnLastUpdated', 'Last updated')}</TableCell>
                <TableCell align="right">{t('columnActions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9}>{t('noSitesMatchFilters', 'No sites match the selected filters.')}</TableCell></TableRow>
              )}
              {rows.map((site) => (
                <TableRow key={site.siteCode}>
                  <TableCell>{site.siteName}</TableCell>
                  <TableCell>{site.clientAccount}</TableCell>
                  <TableCell><Chip label={site.status}
                    size="small"
                    color={READINESS_STATUS_COLOR[site.status]} /></TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Stack direction="row"
                      spacing={1}
                      alignItems="center">
                      <Box sx={{ width: 80 }}>
                        <LinearProgress variant="determinate"
                          value={overallReadiness(site)} />
                      </Box>
                      <Box sx={{ fontSize: '12px' }}>{overallReadiness(site)}%</Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{site.openExceptions}</TableCell>
                  <TableCell>{site.nextBestAction}</TableCell>
                  <TableCell>{site.owner}</TableCell>
                  <TableCell>{site.lastUpdated}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="md"
                      variant="primary"
                      startIcon={<SvgIcon fontSize="small"><PlaylistAddCheck /></SvgIcon>}
                      onClick={() => navigate(`/procurement/readiness/${site.siteCode}`)}
                    >
                      {t('open', 'Open')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </PageArea>
  );
};

export default ProcurementReadinessPage;
